/** @format */

import { ApiFetch } from "../../../shared/utils/fetch";

export interface Category {
  id: string;
  name: string;
  adminId: string;
  createdAt: string;
  updatedAt: string;
}
export interface Color {
  name: string;
  hexValue: string;
  id: string;
}

export interface Size {
  id: string;
  name: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface Material {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/** product-service CollectionDiscountType (PE-2087). */
export type CollectionDiscountType = 'FLAT' | 'PERCENTAGE';

export interface Collection {
  id: string;
  name: string;
  slug: string;
  productCount: number;
  tenantId?: string;
  description?: string | null;
  discountType?: CollectionDiscountType | null;
  /**
   * FLAT is a MINOR-UNIT (kobo) amount off basePrice; PERCENTAGE is 0-100.
   * Never render this as naira without dividing FLAT by 100.
   */
  discountValue?: number | null;
  discountStartsAt?: string | null;
  discountEndsAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Write payload for a collection. Mirrors product-service Create/UpdateCollectionDto:
 * `discountValue` is required whenever `discountType` is set, and passing an explicit
 * `null` on update is how a discount is removed.
 */
export interface CollectionPayload {
  name?: string;
  description?: string | null;
  discountType?: CollectionDiscountType | null;
  discountValue?: number | null;
  discountStartsAt?: string | null;
  discountEndsAt?: string | null;
}

/** Effective price after the best active collection discount (product-service). */
export interface EffectivePrice {
  productId: string;
  basePrice: number;
  effectivePrice: number;
  appliedCollectionId?: string | null;
  discountAmount?: number;
}

/**
 * Occasions taxonomy row (product-service `occasions` domain, proxied by osprey-api).
 * Verified live on osprey-api development 2026-09-22: `GET /occasions` answers with a
 * BARE ARRAY of exactly these fields. `slug` is derived server-side from `name`.
 */
export interface Occasion {
  id: string;
  tenantId?: string;
  name: string;
  slug: string;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Write payload for an occasion. Mirrors product-service Create/UpdateOccasionDto. */
export interface OccasionPayload {
  name: string;
  description?: string;
}

export interface Size_Guide {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
}
export interface Meta {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string;
  endCursor: string;
  totalCount: number;
}
export interface Links {
  first: string;
  next?: string;
}
interface MetaObj {
  meta: Meta;
  links: Links;
}
export interface CategoriesResponse {
  data: Category[];
  meta: Meta;
  links: Links;
}

export interface OccasionsResponse {
  data: Occasion[];
  meta: Meta;
  links: Links;
}

export interface ColorsResponse {
  data: Color[];
  meta: MetaObj;
}

export interface SizeResponse {
  data: Size[];
  meta: MetaObj;
}

export interface MaterialsResponse {
  data: Material[];
  meta: Meta;
  links: Links;
  execution_time_ms: number;
}

export interface CollectionsResponse {
  data: Collection[];
  total: number;
  page: number;
  pageSize: number;
}


export interface SizeGuideResponse {
  execution_time_ms: number;
  result: {
    data: Size_Guide[];
    meta: Meta;
    links: Links;
  };
}

/**
 * Empty page, used when a bare-array response is re-enveloped. `meta`/`links` are
 * synthesised because the upstream sends neither — see `toPage`.
 */
const emptyPageMeta = (count: number): { meta: Meta; links: Links } => ({
  meta: {
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: "",
    endCursor: "",
    totalCount: count,
  },
  links: { first: "" },
});

/**
 * Normalise a list response to the `{ data, meta, links }` page the UI reads.
 *
 * ⚠️ `/categories`, `/collections` and `/occasions` answer with a BARE ARRAY, not a page.
 * osprey-api proxies product-service verbatim and product-service's `findAll` is a plain
 * `prisma.findMany()` (product-service `src/domain/{categories,collections,occasions}`),
 * so nothing ever wraps it. osprey-api's swagger `@ApiOkResponse` example advertises
 * `{ data: [...], total: 1 }` — that example is wrong, and writing the client against it
 * is what broke this: every consumer reads `data?.data`, which on an array is `undefined`,
 * so the Category dropdown on Add/Edit Product and the Categories + Collections metadata
 * tabs rendered EMPTY while the network tab plainly showed the rows.
 *
 * Verified live on osprey-api development 2026-09-22 (MERCHANT_ADMIN):
 *   GET /categories  → Array(7)    GET /collections → Array(4)   GET /occasions → Array(6)
 *   GET /colours|/sizes|/materials → { data, meta, links }   (already a page — unchanged)
 *
 * Accepting BOTH shapes keeps this correct if the backend later adds the envelope its own
 * swagger promises, rather than flipping the bug to the other side.
 */
function toPage<T>(res: T[] | { data: T[] } | null | undefined): {
  data: T[];
  meta: Meta;
  links: Links;
} {
  if (Array.isArray(res)) return { data: res, ...emptyPageMeta(res.length) };
  const data = res?.data ?? [];
  const page = res as Partial<{ data: T[]; meta: Meta; links: Links }> | null | undefined;
  return {
    data,
    meta: page?.meta ?? emptyPageMeta(data.length).meta,
    links: page?.links ?? emptyPageMeta(data.length).links,
  };
}

export const metaDataApi = {
  getAllCategories: async (): Promise<CategoriesResponse> => {
    return toPage<Category>(await ApiFetch.get<Category[] | CategoriesResponse>("/categories"));
  },

  getAllColors: (): Promise<ColorsResponse> => {
    return ApiFetch.get<ColorsResponse>("/colours");
  },
  getAllSizeGuides: (): Promise<SizeGuideResponse> => {
    return ApiFetch.get<SizeGuideResponse>("/size-guides");
  },
  getAllMaterials: (): Promise<MaterialsResponse> => {
    return ApiFetch.get<MaterialsResponse>("/materials");
  },
  getAllSizes: (): Promise<SizeResponse> => {
    return ApiFetch.get<SizeResponse>("/sizes");
  },

  createCategory: (name: string): Promise<Category> => {
    return ApiFetch.post<Category>("/categories", { name });
  },
  updateCategory: (id: string, name: string): Promise<Category> => {
    return ApiFetch.put<Category>(`/categories/${id}`, { name });
  },
  deleteCategory: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/categories/${id}`);
  },
  getAllOccasions: async (): Promise<OccasionsResponse> => {
    return toPage<Occasion>(await ApiFetch.get<Occasion[] | OccasionsResponse>("/occasions"));
  },
  createOccasion: (payload: OccasionPayload): Promise<Occasion> => {
    return ApiFetch.post<Occasion>("/occasions", payload);
  },
  // ApiFetch.put issues a PATCH on the wire, matching PATCH /occasions/:id.
  updateOccasion: (id: string, payload: OccasionPayload): Promise<Occasion> => {
    return ApiFetch.put<Occasion>(`/occasions/${id}`, payload);
  },
  deleteOccasion: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/occasions/${id}`);
  },

  createSize: (name: string, order: number): Promise<Size> => {
    return ApiFetch.post<Size>("/sizes", { name, order });
  },
  updateSize: (id: string, name: string): Promise<Size> => {
    return ApiFetch.put<Size>(`/sizes/${id}`, { name });
  },
  deleteSize: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/sizes/${id}`);
  },
  createMaterial: (name: string): Promise<Material> => {
    return ApiFetch.post<Material>("/materials", { name });
  },
  updateMaterial: (id: string, name: string): Promise<Material> => {
    return ApiFetch.put<Material>(`/materials/${id}`, { name });
  },
  deleteMaterial: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/materials/${id}`);
  },
  createColor: (name: string, hexValue: string): Promise<Color> => {
    return ApiFetch.post<Color>("/colours", { name, hexValue });
  },
  updateColor: (id: string, name: string, hexValue: string): Promise<Color> => {
    return ApiFetch.put<Color>(`/colours/${id}`, { name, hexValue });
  },
  deleteColor: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/colours/${id}`);
  },

  getAllCollections: async (): Promise<CollectionsResponse> => {
    const page = toPage<Collection>(
      await ApiFetch.get<Collection[] | CollectionsResponse>("/collections"),
    );
    return { data: page.data, total: page.meta.totalCount, page: 1, pageSize: page.data.length };
  },
  createCollection: (payload: CollectionPayload): Promise<Collection> => {
    return ApiFetch.post<Collection>("/collections", payload);
  },
  // ApiFetch.put issues a PATCH on the wire, matching PATCH /collections/:id.
  updateCollection: (id: string, payload: CollectionPayload): Promise<Collection> => {
    return ApiFetch.put<Collection>(`/collections/${id}`, payload);
  },
  getEffectivePrice: (productId: string): Promise<EffectivePrice> => {
    return ApiFetch.get<EffectivePrice>(`/products/${productId}/effective-price`);
  },
  deleteCollection: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/collections/${id}`);
  },

  createSizeGuide: (formData: FormData): Promise<Size_Guide> => {
    return ApiFetch.post<Size_Guide>("/size-guides", formData, true);
  },
  updateSizeGuide: (id: string, formData: FormData): Promise<Size_Guide> => {
    return ApiFetch.put<Size_Guide>(`/size-guides/${id}`, formData, true);
  },
  deleteSizeGuide: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/size-guides/${id}`);
  },
};
